<template>
  <el-dialog
    :visible="visible"
    width="900px"
    :close-on-click-modal="false"
    append-to-body
    top="5vh"
    @closed="handleClosed"
    @update:visible="$emit('update:visible', $event)"
  >
    <div style="margin-bottom:15px;">
      <h3 style="text-align:center;margin:0 0 8px;">人工标注 Java 源码</h3>
      <p style="text-align:center;color:#909399;font-size:13px;margin:0;">
        已分类 {{ classifiedCount }} / {{ files.length }} 个文件
      </p>
    </div>

    <!-- 工具栏 -->
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
      <el-checkbox
        :indeterminate="isIndeterminate"
        v-model="checkAll"
        @change="onCheckAllChange"
        style="margin-right:8px;"
      >全选</el-checkbox>
      <el-button size="mini" @click="batchAiAnalyze" :loading="aiBatchLoading">
        <i class="el-icon-magic-stick"></i> AI 自动分析
      </el-button>
      <el-button size="mini" @click="batchApplyRules">
        <i class="el-icon-setting"></i> 应用规则默认值
      </el-button>
      <el-button size="mini" type="primary" @click="submitLabeling">
        <i class="el-icon-check"></i> 保存标注
      </el-button>
    </div>

    <!-- 文件列表 -->
    <div v-if="paginatedFiles.length > 0" style="max-height:500px;overflow-y:auto;">
      <div
        v-for="(file, index) in paginatedFiles"
        :key="file.filePath"
        style="border:1px solid #EBEEF5;border-radius:4px;margin-bottom:6px;padding:8px 12px;"
      >
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <el-checkbox v-model="file._checked" @change="onFileCheckChange" />
          <span
            style="cursor:pointer;font-weight:500;flex:1;min-width:120px;"
            @click="toggleExpand(file)"
          >
            <i :class="expandedFilePath === file.filePath ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" style="margin-right:4px;"></i>
            {{ file.simpleName || file.fileName }}
          </span>
          <el-tag size="mini" :type="tagType(file)">{{ file.type || '未分类' }}</el-tag>
          <el-select
            v-model="file.type"
            size="mini"
            placeholder="选择类型"
            style="width:150px;"
            clearable
          >
            <el-option v-for="t in typeOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
          <el-button
            size="mini"
            icon="el-icon-magic-stick"
            @click="aiAnalyzeSingle(file)"
            :loading="file._aiLoading"
            :disabled="!file.content"
            circle
          ></el-button>
        </div>
        <!-- 展开内容 -->
        <div v-if="expandedFilePath === file.filePath" style="margin-top:8px;border-top:1px solid #EBEEF5;padding-top:8px;">
          <pre style="background:#F5F7FA;padding:8px;border-radius:4px;font-size:12px;max-height:200px;overflow:auto;white-space:pre-wrap;word-break:break-all;margin:0;">{{ file.content ? file.content.substring(0, 3000) : '(无内容)' }}</pre>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <el-empty v-else description="暂无文件" />

    <!-- 分页 -->
    <div style="text-align:center;margin-top:12px;" v-if="files.length > pageSize">
      <el-pagination
        :page-size="pageSize"
        :total="files.length"
        layout="prev, pager, next"
        small
        :current-page.sync="currentPage"
      />
    </div>

    <template #footer>
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <span style="color:#909399;font-size:13px;">
          已分类: {{ classifiedCount }} / {{ files.length }}
        </span>
        <div>
          <el-button @click="$emit('update:visible', false)" size="small">取消</el-button>
          <el-button type="primary" @click="submitLabeling" size="small" :loading="submitting">
            {{ submitting ? '提交中...' : '保存标注' }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
export default {
  name: 'ScanLabelDialog',
  props: {
    visible: Boolean,
    files: { type: Array, default: () => [] },
    aiConfig: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 20,
      expandedFilePath: '',
      checkAll: false,
      isIndeterminate: false,
      aiBatchLoading: false,
      submitting: false,
      typeOptions: [
        { value: 'ENUM', label: '枚举' },
        { value: 'ENTITY', label: '实体' },
        { value: 'VO', label: '视图对象' },
        { value: 'DTO', label: '数据传输对象' },
        { value: 'CONTROLLER', label: '控制器' },
        { value: 'SERVICE', label: '服务' },
        { value: 'SERVICE_IMPL', label: '服务实现' },
        { value: 'MAPPER', label: '映射器' },
        { value: 'CONFIG', label: '配置' },
        { value: 'INTERFACE', label: '接口' },
        { value: 'OTHER', label: '其他' }
      ]
    };
  },
  computed: {
    classifiedCount() {
      return this.files.filter(f => f.type && f.type !== 'OTHER').length;
    },
    paginatedFiles() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.files.slice(start, start + this.pageSize);
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.currentPage = 1;
        this.expandedFilePath = '';
        this.checkAll = false;
        this.isIndeterminate = false;
        // 初始化 _checked
        this.files.forEach(f => { f._checked = f._checked !== undefined ? f._checked : false; });
      }
    }
  },
  methods: {
    tagType(file) {
      if (!file.type || file.type === 'OTHER') return 'info';
      if (file._classifiedBy === 'ai') return 'warning';
      if (file._classifiedBy === 'rule') return 'success';
      return '';
    },
    toggleExpand(file) {
      this.expandedFilePath = this.expandedFilePath === file.filePath ? '' : file.filePath;
    },
    onCheckAllChange(val) {
      this.isIndeterminate = false;
      this.paginatedFiles.forEach(f => { f._checked = val; });
    },
    onFileCheckChange() {
      const checked = this.paginatedFiles.filter(f => f._checked).length;
      const total = this.paginatedFiles.length;
      this.checkAll = checked === total;
      this.isIndeterminate = checked > 0 && checked < total;
    },

    /* ---- AI 分析（单文件） ---- */
    async aiAnalyzeSingle(file) {
      if (!file.content) return;
      this.$set(file, '_aiLoading', true);
      try {
        const type = await this._classifyOne(file);
        if (type) {
          file.type = type;
          file._classifiedBy = 'ai';
        }
      } catch (e) {
        console.error('AI分析失败:', e);
        this.$message.warning('AI分析失败: ' + file.fileName);
      } finally {
        this.$set(file, '_aiLoading', false);
      }
    },

    /* ---- AI 批量分析 ---- */
    async batchAiAnalyze() {
      const targets = this.files.filter(f => (!f.type || f.type === 'OTHER') && f.content);
      if (targets.length === 0) {
        this.$message.info('没有需要分析的文件');
        return;
      }
      this.aiBatchLoading = true;
      try {
        const summaries = targets.map(f => this._buildSummary(f));
        const results = await this._classifyBatch(summaries);
        results.forEach(r => {
          const file = targets.find(f => f.fileName === r.fileName);
          if (file && r.type && r.type !== 'OTHER') {
            file.type = r.type;
            file._classifiedBy = 'ai';
          }
        });
        this.$message.success(`AI 分类完成，已识别 ${results.filter(r => r.type && r.type !== 'OTHER').length} 个文件`);
      } catch (e) {
        console.error('AI批量分析失败:', e);
        this.$message.error('AI分析失败: ' + (e.message || ''));
      } finally {
        this.aiBatchLoading = false;
      }
    },

    /* ---- 应用规则默认值 ---- */
    async batchApplyRules() {
      const targets = this.files.filter(f => (!f.type || f.type === 'OTHER') && f.content);
      if (targets.length === 0) {
        this.$message.info('没有需要分类的文件');
        return;
      }
      let count = 0;
      for (const file of targets) {
        const type = this._ruleClassify(file);
        if (type && type !== 'OTHER') {
          file.type = type;
          file._classifiedBy = 'rule';
          count++;
        }
      }
      this.$message.success(`规则分类完成，已识别 ${count} 个文件`);
    },

    /* ---- 内置规则分类（与 WorkSpace.vue 逻辑一致） ---- */
    _ruleClassify(file) {
      const content = file.content;
      const name = file.fileName;
      if (!content) return null;
      if (content.includes(' enum ')) return 'ENUM';
      if (content.includes(' @TableName') || content.includes('@TableName(')) return 'ENTITY';
      if (name.endsWith('Controller.java')) return 'CONTROLLER';
      if (name.endsWith('ServiceImpl.java')) return 'SERVICE_IMPL';
      if (name.endsWith('Service.java')) return 'SERVICE';
      if (name.endsWith('Mapper.java')) return 'MAPPER';
      if (name.endsWith('Vo.java') || name.endsWith('VO.java')) return 'VO';
      if (name.endsWith('Dto.java') || name.endsWith('DTO.java')) return 'DTO';
      if (name.endsWith('Config.java') || name.endsWith('Configuration.java')) return 'CONFIG';
      if (content.includes(' interface ')) return 'INTERFACE';
      return 'OTHER';
    },

    /* ---- 构建紧凑摘要（供 AI 输入） ---- */
    _buildSummary(file) {
      const content = file.content || '';
      const annotations = (content.match(/@\w+/g) || [])
        .filter(a => !['@Override', '@SuppressWarnings', '@Deprecated'].includes(a))
        .slice(0, 8).join(',');
      const pkg = (content.match(/package\s+([\w.]+);/) || [])[1] || '';
      const isEnum = content.includes(' enum ');
      const isInterface = !isEnum && content.includes(' interface ');
      const extMatch = content.match(/extends\s+(\w+)/);
      const implMatch = content.match(/implements\s+([\w,\s]+)/);
      return `fileName:${file.fileName}|pkg:${pkg}|ann:${annotations}|ext:${extMatch ? extMatch[1] : '-'}|impl:${implMatch ? implMatch[1].replace(/,\s*/g, ',') : '-'}|enum:${isEnum}|inf:${isInterface}`;
    },

    /* ---- 调用 AI 分类（单个） ---- */
    async _classifyOne(file) {
      const config = this.aiConfig || {};
      const endpoint = config.endpoint || '/v1/chat/completions';
      const model = config.model || 'ernie-4.0';
      const apiKey = config.apiKey || '';

      // 紧凑输入：摘要 + 前30行
      const lines = (file.content || '').split('\n').slice(0, 30).join('\n');
      const summary = this._buildSummary(file);
      const input = summary + '\n--- file head (30 lines) ---\n' + lines;

      const headers = { 'Content-Type': 'application/json' };
      if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        signal: AbortSignal.timeout(15000),
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: this._aiSystemPrompt() },
            { role: 'user', content: '请分类：\n' + input }
          ],
          stream: false
        })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const result = await res.json();
      const text = result.data || result.choices?.[0]?.message?.content || '';
      const cleaned = text.replace(/```(?:json)?\s*([\s\S]*?)\s*```/, '$1').trim();
      try {
        const parsed = JSON.parse(cleaned);
        return parsed.type || null;
      } catch {
        const m = cleaned.match(/"type"\s*:\s*"(\w+)"/);
        return m ? m[1] : null;
      }
    },

    /* ---- 调用 AI 分类（批量） ---- */
    async _classifyBatch(summaries) {
      const config = this.aiConfig || {};
      const endpoint = config.endpoint || '/v1/chat/completions';
      const model = config.model || 'ernie-4.0';
      const apiKey = config.apiKey || '';

      const headers = { 'Content-Type': 'application/json' };
      if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        signal: AbortSignal.timeout(30000),
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: this._aiSystemPrompt() },
            { role: 'user', content: '请分类以下文件（只返回JSON数组）：\n' + summaries.join('\n') }
          ],
          stream: false
        })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const result = await res.json();
      const text = result.data || result.choices?.[0]?.message?.content || '';
      const cleaned = text.replace(/```(?:json)?\s*([\s\S]*?)\s*```/g, '$1').trim();
      try {
        return JSON.parse(cleaned);
      } catch {
        this.$message.warning('AI 返回格式异常，尝试逐条解析');
        return [];
      }
    },

    _aiSystemPrompt() {
      return '你是一个Java源码分类专家。将Java源文件分类到以下类型之一：\n' +
        'ENUM(枚举), ENTITY(实体,含@TableName), VO(视图对象), DTO(数据传输对象), ' +
        'CONTROLLER(控制器), SERVICE(服务接口), SERVICE_IMPL(服务实现), MAPPER(映射器), ' +
        'CONFIG(配置类), INTERFACE(接口/抽象类), OTHER(其他)\n' +
        '规则：文件名后缀和包路径是关键信号。只返回JSON：{"fileName":"","type":""} 或 [{"fileName":"","type":""}]';
    },

    /* ---- 保存标注 ---- */
    submitLabeling() {
      const unclassified = this.files.filter(f => !f.type || f.type === 'OTHER');
      if (unclassified.length > 0) {
        this.$confirm(unclassified.length + ' 个文件尚未分类，确定保存？', '提示', {
          confirmButtonText: '保存',
          cancelButtonText: '继续标注',
          type: 'warning'
        }).then(() => {
          this.$emit('confirm', this.files);
          this.$emit('update:visible', false);
        }).catch(() => {});
      } else {
        this.$emit('confirm', this.files);
        this.$emit('update:visible', false);
      }
    },

    handleClosed() {
      this.expandedFilePath = '';
      this.currentPage = 1;
      this.checkAll = false;
      this.isIndeterminate = false;
    }
  }
};
</script>
